import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import {
  Shield, UploadSimple, ArrowCounterClockwise, FloppyDisk, Plus, CopySimple, DownloadSimple,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Toaster, toast } from "@/components/ui/sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { NavTabs } from "@/components/NavTabs";
import { SapConnectionStatus } from "@/components/SapConnectionStatus";
import { ErpConnectionStatus } from "@/components/ErpConnectionStatus";
import { ContextSummary, ModelPicker } from "@/components/routing/ModelPicker";
import { IssueList, SapPlanPreview, StepReviewTable } from "@/components/routing/StepReviewTable";
import { PostProgress } from "@/components/routing/PostProgress";
import { BooPicker } from "@/components/routing/BooPicker";
import { LinkPanel } from "@/components/routing/LinkPanel";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/routing-builder`;

const TABS = [
  { id: "build", label: "1. Build the routing" },
  { id: "save", label: "2. Save it in SAP" },
  { id: "link", label: "3. Link it to the model" },
];

const BLANK_STEP = { description: "", step_type: "Production", resource_id: "", duration_minutes: "",
                     component_label: "", reporting_point: "Auto" };

const errorText = (error, fallback) => {
  const detail = error?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (detail?.message) return [detail.message, ...(detail.errors || [])].join(" ");
  return fallback;
};

const renumber = (rows) => rows.map((step, i) => ({ ...step, step_no: (i + 1) * 10 }));

const Section = ({ number, title, hint, children, disabled }) => (
  <section className={`relative pl-10 pb-6 ${disabled ? "opacity-50 pointer-events-none" : ""}`}>
    <div className="absolute left-0 top-0 flex flex-col items-center h-full">
      <div className="w-7 h-7 rounded-full bg-[#0E7C86] text-white text-[12px] font-semibold flex items-center justify-center shrink-0">
        {number}
      </div>
      <div className="w-px flex-1 bg-[#D0D5DD] mt-1" />
    </div>
    <h2 className="font-heading text-[15px] font-semibold text-[#1D2939]">{title}</h2>
    {hint && <p className="text-[12px] text-[#667085] mt-0.5 mb-3">{hint}</p>}
    {children}
  </section>
);

export default function RoutingBuilderPage() {
  const [query, setQuery] = useState("");
  const [products, setProducts] = useState([]);
  const [productId, setProductId] = useState("");
  const [models, setModels] = useState([]);
  const [loadingModels, setLoadingModels] = useState(false);
  const [model, setModel] = useState(null);
  const [context, setContext] = useState(null);
  const [loadingContext, setLoadingContext] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [steps, setSteps] = useState([]);
  const [review, setReview] = useState(null);
  const [tab, setTab] = useState("build");

  const [booId, setBooId] = useState("");
  const [booDescription, setBooDescription] = useState("");
  const [saving, setSaving] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [savedBooId, setSavedBooId] = useState("");

  const [boos, setBoos] = useState([]);
  const [booQuery, setBooQuery] = useState("");
  const [loadingBoos, setLoadingBoos] = useState(false);
  const [copyOpen, setCopyOpen] = useState(false);
  const [copyBusy, setCopyBusy] = useState(false);

  const [linkBooId, setLinkBooId] = useState("");
  const [linkDetails, setLinkDetails] = useState(null);
  const [loadingLink, setLoadingLink] = useState(false);
  const [mappings, setMappings] = useState({});
  const [linkOverrides, setLinkOverrides] = useState({});
  const [linkAck, setLinkAck] = useState(false);
  const [linking, setLinking] = useState(false);

  const [run, setRun] = useState(null);
  const [verifyingByproducts, setVerifyingByproducts] = useState(false);
  const fileRef = useRef(null);
  const pollRef = useRef(null);

  useEffect(() => {
    if (query.trim().length < 2) {
      setProducts([]);
      return undefined;
    }
    const timer = setTimeout(async () => {
      try {
        const { data } = await axios.get(`${API}/products`, { params: { q: query.trim() } });
        setProducts(data);
      } catch {
        setProducts([]);
      }
    }, 250);
    return () => clearTimeout(timer);
  }, [query]);

  const resetRouting = () => {
    setSteps([]);
    setReview(null);
    setRun(null);
    setSavedBooId("");
    setLinkDetails(null);
    setLinkBooId("");
    setMappings({});
    setLinkOverrides({});
    setLinkAck(false);
    setTab("build");
  };

  const pickProduct = async (product) => {
    setProductId(product.product_id);
    setQuery(product.product_id);
    setProducts([]);
    setModel(null);
    setContext(null);
    resetRouting();
    setLoadingModels(true);
    try {
      const { data } = await axios.get(`${API}/models`, { params: { product_id: product.product_id } });
      setModels(data);
      if (!data.length) toast.error("SAP has no production model for this part.");
    } catch (error) {
      toast.error(errorText(error, "Could not read the production models from SAP."));
      setModels([]);
    } finally {
      setLoadingModels(false);
    }
  };

  const selectModel = async (picked) => {
    setModel(picked);
    setContext(null);
    resetRouting();
    setLoadingContext(true);
    try {
      const { data } = await axios.get(`${API}/context`, { params: { model_id: picked.model_id } });
      setContext(data);
      setBooId(data.suggested_boo_id || "");
      setBooDescription(`Routing for ${data.model?.product_id || picked.product_id || ""}`);
      (data.warnings || []).forEach((warning) => toast.warning(warning, { duration: 8000 }));
      // A by-product the supervisor still owes SAP must survive closing the tab,
      // so the last posting is restored whenever it left one outstanding.
      try {
        const { data: runs } = await axios.get(`${API}/runs`, {
          params: { model_id: picked.model_id, limit: 1 },
        });
        if (runs?.[0]?.manual_actions?.length) setRun(runs[0]);
      } catch {
        /* the history is a convenience - never block the page on it */
      }
    } catch (error) {
      toast.error(errorText(error, "Could not read this production model from SAP."));
      setModel(null);
    } finally {
      setLoadingContext(false);
    }
  };

  const revalidate = useCallback(async (nextSteps) => {
    if (!model || !nextSteps.length) return;
    try {
      const { data } = await axios.post(`${API}/preview`, {
        model_id: model.model_id,
        steps: nextSteps.map((step) => ({
          step_no: String(step.step_no),
          description: step.description,
          step_type: step.step_type,
          resource_id: step.resource_id,
          duration_minutes: step.duration_minutes,
          component_label: step.component_label,
          reporting_point: step.reporting_point,
          ai_classified: step.ai_classified,
          ai_confidence: step.ai_confidence,
        })),
      });
      setReview(data);
      // The server is the authority on step type: it silently moves a
      // non-last Final Inspection back to Production (SAP only allows an
      // inspection last), so the dropdown must show what will actually be
      // created, not what was asked for.
      setSteps((current) =>
        current.map((step, i) => ({
          ...step,
          step_type: data.steps[i]?.step_type ?? step.step_type,
          logistics_area_id: data.steps[i]?.logistics_area_id,
        })),
      );
    } catch (error) {
      toast.error(errorText(error, "Could not re-check the steps."));
    }
  }, [model]);

  const applySteps = (next) => {
    const rows = renumber(next);
    setSteps(rows);
    if (rows.length) revalidate(rows);
    else setReview(null);
    return rows;
  };

  const changeStep = (index, patch) => {
    setSteps((current) => {
      const next = current.map((step, i) => (i === index ? { ...step, ...patch } : step));
      clearTimeout(pollRef.current);
      pollRef.current = setTimeout(() => revalidate(next), 500);
      return next;
    });
  };

  const removeStep = (index) => applySteps(steps.filter((_, i) => i !== index));
  const addStep = () => applySteps([...steps, { ...BLANK_STEP }]);
  const insertStep = (index) => applySteps([...steps.slice(0, index), { ...BLANK_STEP }, ...steps.slice(index)]);
  const duplicateStep = (index) =>
    applySteps([...steps.slice(0, index + 1), { ...steps[index] }, ...steps.slice(index + 1)]);

  const downloadTemplate = async () => {
    setDownloading(true);
    try {
      const response = await axios.get(`${API}/template`, {
        params: { model_id: model.model_id },
        responseType: "blob",
      });
      const url = URL.createObjectURL(response.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = `routing-${model.model_id}.xlsx`;
      link.click();
      URL.revokeObjectURL(url);
      toast.success("Template downloaded. Fill in the steps, then upload it below.");
    } catch (error) {
      toast.error(errorText(error, "Could not build the template."));
    } finally {
      setDownloading(false);
    }
  };

  const uploadSheet = async (file) => {
    if (!file) return;
    setUploading(true);
    setRun(null);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await axios.post(`${API}/import`, form, { params: { model_id: model.model_id } });
      setSteps(data.steps.map((step) => ({ ...step, component_label: step.component?.label || "" })));
      setReview(data);
      if (data.suggested_boo_id) setBooId(data.suggested_boo_id);
      toast.success(`${data.steps.length} step(s) read from ${file.name}`);
    } catch (error) {
      toast.error(errorText(error, "That sheet could not be read."));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const loadBoos = useCallback(async (search) => {
    setLoadingBoos(true);
    try {
      const { data } = await axios.get(`${API}/boos`, { params: search ? { q: search } : {} });
      setBoos(data);
    } catch (error) {
      toast.error(errorText(error, "Could not list the bills of operations in SAP."));
      setBoos([]);
    } finally {
      setLoadingBoos(false);
    }
  }, []);

  useEffect(() => {
    if (tab !== "link" && !copyOpen) return undefined;
    const timer = setTimeout(() => loadBoos(booQuery.trim()), booQuery ? 400 : 0);
    return () => clearTimeout(timer);
  }, [tab, copyOpen, booQuery, loadBoos]);

  const copyFromBoo = async (sourceId) => {
    setCopyBusy(true);
    try {
      const { data } = await axios.get(`${API}/boos/${encodeURIComponent(sourceId)}/as-steps`, {
        params: { model_id: model.model_id },
      });
      setSteps(data.steps.map((step) => ({ ...step, component_label: step.component?.label || "" })));
      setReview({ ...data, can_post: !data.errors?.length });
      setCopyOpen(false);
      toast.success(`${data.steps.length} step(s) copied from ${sourceId}. Edit them, then save as a new routing.`);
    } catch (error) {
      toast.error(errorText(error, "Could not read that routing from SAP."));
    } finally {
      setCopyBusy(false);
    }
  };

  const saveBoo = async () => {
    setConfirmOpen(false);
    setSaving(true);
    setRun(null);
    try {
      const { data } = await axios.post(`${API}/save-boo`, {
        model_id: model.model_id,
        boo_id: booId,
        boo_description: booDescription || `Routing for ${model.product_id || productId}`,
        steps: steps.map((step) => ({
          step_no: String(step.step_no),
          description: step.description,
          step_type: step.step_type,
          resource_id: step.resource_id,
          duration_minutes: step.duration_minutes,
          component_label: step.component_label,
          reporting_point: step.reporting_point,
        })),
      });
      setRun({ ...data, stages: [] });
    } catch (error) {
      setSaving(false);
      toast.error(errorText(error, "SAP would not accept this routing."));
    }
  };

  const selectLinkBoo = useCallback(async (id, prefill) => {
    setLinkBooId(id);
    setLinkDetails(null);
    setLinkAck(false);
    setLinkOverrides({});
    setLoadingLink(true);
    try {
      const { data } = await axios.get(`${API}/boos/${encodeURIComponent(id)}/operations`);
      setLinkDetails(data);
      // A routing built here already knows which step consumes what, so the
      // backflush points are carried over instead of being re-picked by hand.
      const production = (data.operations || []).filter((op) => !op.is_inspection);
      const next = {};
      if (prefill) {
        steps.forEach((step, index) => {
          const group = (context?.item_groups || []).find((g) => g.label === step.component_label);
          const element = production.find((op) => op.element_id === `OP_${String((index + 1) * 10).padStart(3, "0")}`);
          if (group && element) next[group.item_group_id] = element.element_id;
        });
      }
      setMappings(next);
    } catch (error) {
      toast.error(errorText(error, "Could not read that routing from SAP."));
    } finally {
      setLoadingLink(false);
    }
  }, [steps, context]);

  const linkByproductPlan = (context?.byproducts || []).map((byproduct) => {
    const production = (linkDetails?.operations || []).filter((op) => !op.is_inspection);
    const chosen = linkOverrides[byproduct.product_id]
      || production.find((op) => op.element_id === byproduct.element_id)?.element_id
      || production[0]?.element_id
      || "";
    return {
      product_id: byproduct.product_id,
      quantity: byproduct.quantity,
      was_at_element_id: byproduct.element_id,
      was_at_activity_id: byproduct.activity_id,
      element_id: chosen,
      unchanged: chosen === byproduct.element_id,
    };
  });

  const linkNow = async () => {
    setLinking(true);
    setRun(null);
    try {
      const { data } = await axios.post(`${API}/link`, {
        model_id: model.model_id,
        boo_id: linkBooId,
        byproducts_acknowledged: linkAck,
        byproduct_overrides: Object.fromEntries(linkByproductPlan.map((p) => [p.product_id, p.element_id])),
        mappings: Object.entries(mappings)
          .filter(([, element]) => element)
          .map(([item_group_id, element_id]) => ({ item_group_id, element_id })),
      });
      setRun({ ...data, stages: [] });
    } catch (error) {
      setLinking(false);
      toast.error(errorText(error, "SAP would not link this routing."));
    }
  };

  useEffect(() => {
    if (!run?.run_id || run.status !== "running") return undefined;
    const timer = setInterval(async () => {
      try {
        const { data } = await axios.get(`${API}/runs/${run.run_id}`);
        setRun(data);
        if (data.status !== "running") {
          setSaving(false);
          setLinking(false);
          if (data.ok) {
            toast.success(data.message);
            if (data.phase === "save") {
              setSavedBooId(data.boo_id);
              setTab("link");
              selectLinkBoo(data.boo_id, true);
            }
          } else {
            toast.error(data.message, { duration: 12000 });
          }
        }
      } catch {
        /* keep polling - a single dropped poll is not a failure */
      }
    }, 4000);
    return () => clearInterval(timer);
  }, [run, selectLinkBoo]);

  const verifyByproducts = async () => {
    if (!run?.run_id) return;
    setVerifyingByproducts(true);
    try {
      const { data } = await axios.post(`${API}/runs/${run.run_id}/byproduct-check`);
      setRun((prev) => ({
        ...prev,
        byproduct_check: data,
        manual_actions: data.all_assigned ? [] : prev.manual_actions,
      }));
      if (data.all_assigned) toast.success("SAP confirms the by-product assignment is in place.");
      else toast.error("SAP still does not show that assignment - check it was saved.", { duration: 10000 });
    } catch (error) {
      toast.error(errorText(error, "Could not read the model back from SAP."));
    } finally {
      setVerifyingByproducts(false);
    }
  };

  const canSave = Boolean(steps.length && booId && !review?.errors?.length && !saving);

  return (
    <div className="h-screen flex flex-col overflow-hidden bg-[#F2F4F7] text-[#1D2939]">
      <Toaster position="top-right" />
      <header className="min-h-16 bg-[#0E7C86] shadow-[0_1px_3px_0_rgba(16,24,40,0.15)] flex items-center justify-between px-3 sm:px-5 shrink-0 z-10 gap-2 sm:gap-4">
        <div className="flex items-center gap-3 shrink-0" data-testid="app-title">
          <div className="w-8 h-8 rounded-lg bg-white/15 flex items-center justify-center shrink-0">
            <Shield size={18} weight="fill" className="text-white" />
          </div>
          <div className="flex flex-col leading-tight">
            <span className="font-heading text-[16px] font-bold text-white tracking-tight">Production Control</span>
            <span className="font-sans text-[12px] text-white/70 hidden sm:inline">Routing Builder</span>
          </div>
        </div>
        <div className="w-px h-7 bg-white/25 shrink-0" />
        <div className="flex items-center gap-3 flex-1 justify-start min-w-0">
          <NavTabs />
        </div>
        <SapConnectionStatus />
        <ErpConnectionStatus />
      </header>

      <main className="flex-1 overflow-y-auto p-4 sm:p-6" data-testid="routing-builder-page">
        <div className="max-w-6xl">
          <Section
            number="1"
            title="Choose the part and its production model"
            hint="The routing is built for one production model. Nothing changes in SAP until you save it, and the model keeps its current routing until you link the new one."
          >
            <ModelPicker
              products={products}
              query={query}
              onQuery={setQuery}
              onPickProduct={pickProduct}
              productId={productId}
              models={models}
              loadingModels={loadingModels}
              selectedModel={model}
              busy={loadingContext || saving || linking}
              onSelectModel={selectModel}
            />
            {loadingContext && (
              <p className="mt-3 text-[13px] text-[#475467]" data-testid="routing-context-loading">
                Reading the model, its bill of material and the resources at its site from SAP…
              </p>
            )}
          </Section>

          {context && (
            <div className="pl-10">
              <div className="flex flex-wrap gap-1 border-b border-[#D0D5DD] mb-4" data-testid="routing-tabs">
                {TABS.map((entry) => (
                  <button
                    key={entry.id}
                    type="button"
                    onClick={() => setTab(entry.id)}
                    data-testid={`routing-tab-${entry.id}`}
                    className={`px-3 py-2 text-[13px] font-medium border-b-2 -mb-px transition-colors ${
                      tab === entry.id
                        ? "border-[#0E7C86] text-[#0E7C86]"
                        : "border-transparent text-[#667085] hover:text-[#1D2939]"
                    }`}
                  >
                    {entry.label}
                  </button>
                ))}
              </div>

              {tab === "build" && (
                <div className="space-y-3" data-testid="routing-build-tab">
                  <div className="flex flex-wrap items-center gap-2">
                    <Button
                      onClick={addStep}
                      data-testid="routing-add-step"
                      className="h-9 bg-[#0E7C86] hover:bg-[#0B656D] text-white text-[13px]"
                    >
                      <Plus size={15} className="mr-1.5" /> Add a step
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => setCopyOpen(true)}
                      data-testid="routing-copy-boo-open"
                      className="h-9 text-[13px] border-[#D0D5DD]"
                    >
                      <CopySimple size={15} className="mr-1.5" /> Start from an existing routing
                    </Button>
                    <input
                      ref={fileRef}
                      type="file"
                      accept=".xlsx,.xlsm"
                      onChange={(e) => uploadSheet(e.target.files?.[0])}
                      data-testid="routing-upload-input"
                      className="hidden"
                    />
                    <Button
                      variant="outline"
                      onClick={() => fileRef.current?.click()}
                      disabled={uploading}
                      data-testid="routing-upload-button"
                      className="h-9 text-[13px] border-[#D0D5DD]"
                    >
                      <UploadSimple size={15} className="mr-1.5" />
                      {uploading ? "Reading the sheet…" : "Import from Excel"}
                    </Button>
                    <Button
                      variant="ghost"
                      onClick={downloadTemplate}
                      disabled={downloading}
                      data-testid="routing-download-template-tab"
                      className="h-9 text-[13px] text-[#475467]"
                    >
                      <DownloadSimple size={15} className="mr-1.5" />
                      {downloading ? "Building…" : "Blank template"}
                    </Button>
                    {steps.length > 0 && (
                      <Button
                        variant="ghost"
                        onClick={resetRouting}
                        data-testid="routing-start-over"
                        className="h-9 text-[13px] text-[#475467]"
                      >
                        <ArrowCounterClockwise size={15} className="mr-1.5" /> Clear
                      </Button>
                    )}
                  </div>

                  <ContextSummary context={context} onDownload={downloadTemplate} downloading={downloading} />

                  {steps.length === 0 ? (
                    <p className="text-[13px] text-[#667085]" data-testid="routing-no-steps">
                      No steps yet. Add them one at a time, copy a routing that already works, or import the Excel
                      template.
                    </p>
                  ) : (
                    <>
                      <StepReviewTable
                        steps={steps}
                        context={context}
                        onChange={changeStep}
                        onRemove={removeStep}
                        onInsert={insertStep}
                        onDuplicate={duplicateStep}
                      />
                      <IssueList errors={review?.errors} warnings={review?.warnings} />
                      {review?.elements?.length > 0 && (
                        <div>
                          <div className="text-[11px] font-semibold text-[#475467] uppercase tracking-wide mb-1.5">
                            What will be created in SAP
                          </div>
                          <SapPlanPreview elements={review.elements} />
                        </div>
                      )}
                      <Button
                        onClick={() => setTab("save")}
                        disabled={!!review?.errors?.length}
                        data-testid="routing-to-save"
                        className="h-9 bg-[#1D2939] hover:bg-[#101828] text-white text-[13px] disabled:opacity-50"
                      >
                        Next: save it in SAP
                      </Button>
                    </>
                  )}
                </div>
              )}

              {tab === "save" && (
                <div className="space-y-3" data-testid="routing-save-tab">
                  <p className="text-[13px] text-[#475467]">
                    This creates the routing in SAP and checks SAP finds it consistent. Production model{" "}
                    <span className="font-semibold">{model?.model_id}</span> is not touched - you link it in the next
                    step, once you can see it was created cleanly.
                  </p>
                  <div className="flex flex-wrap items-end gap-3">
                    <div>
                      <label className="text-[11px] font-semibold text-[#475467] uppercase tracking-wide">
                        Bill of operations ID
                      </label>
                      <input
                        value={booId}
                        onChange={(e) => setBooId(e.target.value.toUpperCase())}
                        data-testid="routing-boo-id"
                        className="mt-1 block w-56 h-9 rounded-lg border border-[#D0D5DD] px-3 text-[13px] font-mono outline-none focus:border-[#0E7C86] focus:ring-1 focus:ring-[#0E7C86] transition-colors"
                      />
                    </div>
                    <div className="flex-1 min-w-[220px]">
                      <label className="text-[11px] font-semibold text-[#475467] uppercase tracking-wide">
                        Description
                      </label>
                      <input
                        value={booDescription}
                        onChange={(e) => setBooDescription(e.target.value)}
                        data-testid="routing-boo-description"
                        className="mt-1 block w-full h-9 rounded-lg border border-[#D0D5DD] px-3 text-[13px] outline-none focus:border-[#0E7C86] focus:ring-1 focus:ring-[#0E7C86] transition-colors"
                      />
                    </div>
                    <Button
                      onClick={() => setConfirmOpen(true)}
                      disabled={!canSave}
                      data-testid="routing-save-boo-button"
                      className="h-9 bg-[#0E7C86] hover:bg-[#0B656D] text-white text-[13px] disabled:opacity-50"
                    >
                      <FloppyDisk size={15} className="mr-1.5" />
                      {saving ? "Creating in SAP…" : "Create in SAP"}
                    </Button>
                  </div>
                  <IssueList errors={review?.errors} />
                  {savedBooId && (
                    <p className="text-[12px] text-[#027A48]" data-testid="routing-saved-note">
                      {savedBooId} exists in SAP and is consistent. SAP will not let it be edited afterwards - a change
                      means saving a new routing under a new ID.
                    </p>
                  )}
                  {run?.phase === "save" && (
                    <PostProgress run={run} onVerifyByproducts={verifyByproducts} verifying={verifyingByproducts} />
                  )}
                </div>
              )}

              {tab === "link" && (
                <div className="space-y-3" data-testid="routing-link-tab">
                  <p className="text-[13px] text-[#475467]">
                    Pick the routing to attach to <span className="font-semibold">{model?.model_id}</span>. Any bill of
                    operations in SAP can be linked, including one built here earlier or made directly in SAP.
                  </p>
                  <BooPicker
                    boos={boos}
                    loading={loadingBoos}
                    query={booQuery}
                    onQuery={setBooQuery}
                    selected={linkBooId}
                    onSelect={(id) => selectLinkBoo(id, id === savedBooId)}
                    testid="routing-link-boo"
                  />
                  {loadingLink && (
                    <p className="text-[12px] text-[#667085]">Reading that routing's operations from SAP…</p>
                  )}
                  <LinkPanel
                    details={linkDetails}
                    context={context}
                    modelId={model?.model_id}
                    mappings={mappings}
                    onMapping={(groupId, elementId) =>
                      setMappings((current) => ({ ...current, [groupId]: elementId }))}
                    byproductPlan={linkByproductPlan}
                    acknowledged={linkAck}
                    onAcknowledge={setLinkAck}
                    onOverride={(product, elementId) =>
                      setLinkOverrides((current) => ({ ...current, [product]: elementId }))}
                    onLink={linkNow}
                    linking={linking}
                  />
                  {run?.phase === "link" && (
                    <PostProgress run={run} onVerifyByproducts={verifyByproducts} verifying={verifyingByproducts} />
                  )}
                </div>
              )}
            </div>
          )}

          {run && !run.phase && (
            <PostProgress run={run} onVerifyByproducts={verifyByproducts} verifying={verifyingByproducts} />
          )}
        </div>
      </main>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent data-testid="routing-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Create this routing in SAP?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="text-[13px] text-[#475467] space-y-2">
                <p>
                  Bill of operations <span className="font-mono font-semibold">{booId}</span> will be created with{" "}
                  {steps.length} step(s). Production model{" "}
                  <span className="font-semibold">{model?.model_id}</span> is not changed by this.
                </p>
                <p>SAP only supports creating a routing, never editing one - a later change means a new ID.</p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="routing-confirm-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={saveBoo}
              data-testid="routing-confirm-post"
              className="bg-[#0E7C86] hover:bg-[#0B656D]"
            >
              Create in SAP
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={copyOpen} onOpenChange={setCopyOpen}>
        <AlertDialogContent data-testid="routing-copy-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Start from an existing routing</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="text-[13px] text-[#475467]">
                Its steps are copied in so you can edit them and save as a new routing. A resource that does not
                exist at site {context?.model?.site_id} is left blank for you to pick.
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <BooPicker
            boos={boos}
            loading={loadingBoos || copyBusy}
            query={booQuery}
            onQuery={setBooQuery}
            selected=""
            onSelect={copyFromBoo}
            testid="routing-copy-boo"
          />
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="routing-copy-cancel">Cancel</AlertDialogCancel>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
